import { FC, useState, useRef, useEffect } from 'react'
import { SingleSelectFormControl, CategoryMenuProps } from './styles/singleSelectField.styles'
import { TrendsChartSelectDropdownIcon } from 'ui/components/svg'
import { ListItemText, MenuItem, InputLabel, SelectProps, Select } from '@mui/material'
import theme from 'theme'

interface Props extends SelectProps {
  options: string[]
  value: any
  handleChange: any
  minWidth?: string
  labeltext?: string
  disabled: boolean
  raise?: boolean
  modalRaise?: boolean
  label?: string
  fullWidth?: boolean
  licenseYearField?: boolean
}

const SingleSelectField: FC<Props> = ({
  options,
  labeltext,
  handleChange,
  placeholder,
  minWidth,
  value,
  disabled,
  raise,
  modalRaise,
  label,
  fullWidth,
  licenseYearField,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const inputRef = useRef<HTMLDivElement>(null)
  const [inputWidth, setInputWidth] = useState<string | number>('auto')

  useEffect(() => {
    const updateWidth = () => {
      if (inputRef.current) {
        setInputWidth(inputRef.current.offsetWidth)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const handleClick = (event: any) => {
    if (disabled) return
    setAnchorEl(event.currentTarget)
  }

  return (
    <SingleSelectFormControl
      sx={{ minWidth, width: fullWidth ? '100%' : undefined }}
      is_opened={anchorEl ? 'true' : 'false'}
      is_disabled={disabled ? 'true' : 'false'}
      variant="outlined"
    >
      {label && (
        <InputLabel id={`single-select-${label}-label`} shrink>
          {label}
        </InputLabel>
      )}
      <Select
        labelId={`single-select-${label}-label`}
        id={`single-select-${label}`}
        displayEmpty
        notched={!!label}
        MenuProps={{
          ...CategoryMenuProps,
          sx: {
            zIndex: raise ? 3001 : modalRaise ? 100005 : ''
          },
          PaperProps: {
            ...CategoryMenuProps.PaperProps,
            sx: {
              ...CategoryMenuProps.PaperProps.sx,
              display: anchorEl ? 'block' : 'none',
              width: `${inputWidth}px`,
              minWidth: 'unset',
              marginTop: '4px'
            },
            style: {
              zIndex: raise ? 3001 : ''
            }
          },
          anchorEl,
          open: Boolean(anchorEl),
          onClose: () => setAnchorEl(null)
        }}
        sx={{
          minWidth,
          '& .MuiSelect-icon': { transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)' }
        }}
        IconComponent={(props) => <TrendsChartSelectDropdownIcon {...props} />}
        renderValue={(selected) => {
          if (Array.isArray(selected)) {
            const pruned = selected.filter((s: string) => s !== '')
            if (pruned.length === 0) {
              return <span style={{ color: '#938F99' }}>{placeholder}</span>
            }
            if (pruned.length === 1) {
              return `${pruned[0]} +${pruned.length - 1}`
            }
            return pruned[0]
          }
          if (!selected) {
            return (
              <span
                style={{
                  color: '#D0C3CC',
                  fontFamily: 'Quicksand, sans-serif',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px'
                }}
              >
                {placeholder}
              </span>
            )
          }
          if (labeltext) {
            return selected
          }
          return selected
        }}
        {...rest}
        value={value}
        label={label}
        onChange={(e) => {
          setAnchorEl(null)
          handleChange(e)

          if (inputRef.current) {
            inputRef.current.blur()
          }
        }}
        onMouseDown={handleClick}
        ref={inputRef}
      >
        {options.map((option) => {
          let currentYear = ''
          if (licenseYearField) {
            const trimmed = option.split('/')
            const c = trimmed[0]
            if (c.includes(String(new Date().getFullYear()))) {
              currentYear = c
            }
          }
          return (
            <MenuItem key={option} value={option} disableRipple>
              <ListItemText
                primary={option}
                sx={{
                  flexGrow: licenseYearField && currentYear ? '0 !important' : '1 !important'
                }}
              />
              {licenseYearField && currentYear && <span className="current-year">Current year</span>}
              {value === option && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className="current-year-svg"
                >
                  <path
                    d="M2.625 7.4375L6.125 10.9375L11.375 3.0625"
                    stroke={theme.colors.textPrimary}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </MenuItem>
          )
        })}
      </Select>
    </SingleSelectFormControl>
  )
}

export default SingleSelectField
